/**
 * The weather overlay: whether it is up, what it is showing, and when.
 *
 * The shape Edd asked for is a panel you set up and then get out of your way: pick
 * a model and a variable, minimise it, and you are left with a bar to nudge
 * through the hours while you look at the chart. Pull the panel back whenever you
 * want to change something, and one button to turn the lot off.
 *
 * This store is the UI's state and nothing more. The actual drawing still runs off
 * the environment store, which MapView already reads, so turning the overlay on
 * here writes through to `overlayMode`, `weatherSource` and `weatherForecastH`
 * rather than adding a second, parallel way for the map to be told what to draw.
 * Two sources of truth about what is on the chart is precisely the bug this app
 * cannot afford.
 *
 * Model and variable persist. The forecast hour does not: coming back to the app
 * on a different day and finding it showing "+37 h" from last time would be a
 * quietly wrong answer to "what is it doing now".
 */
import { create } from 'zustand';
import { OVERLAY_MODELS, WAVE_MODELS, modelById } from '@/weather/models';
import { loadJson, saveJson } from '@/store/persist';

const MODEL_LS = 'acp.overlayModel.v1';
const VAR_LS = 'acp.overlayVariable.v1';
const STYLE_LS = 'acp.overlayWindStyle.v1';
const WAVE_LS = 'acp.overlayWaveModel.v1';

/** What the overlay can draw. Only wind has a renderer so far; see `drawn`. */
export interface OverlayVariable {
  id: string;
  name: string;
  /** False until there is a layer that draws it. Honest, rather than a dead button. */
  drawn: boolean;
  note: string;
}

export const OVERLAY_VARIABLES: OverlayVariable[] = [
  {
    id: 'wind',
    name: 'Wind',
    drawn: true,
    note: 'Wind at 10 m, washed across the chart, with arrows showing which way it is blowing.',
  },
  {
    id: 'gust',
    name: 'Gusts',
    drawn: true,
    note: 'The strongest gust in the hour, on the same colour scale as the wind so the two compare at a glance.',
  },
  {
    id: 'rain',
    name: 'Rain',
    drawn: true,
    note: 'Rainfall in the hour. Dry ground is left clear so the chart underneath stays readable.',
  },
  {
    id: 'pressure',
    name: 'Pressure',
    drawn: true,
    note: 'Sea-level pressure. Where the colour changes fastest is where the wind is.',
  },
  {
    id: 'waves',
    name: 'Waves',
    drawn: true,
    note: 'Significant wave height, washed across the chart, from the wave model picked below.',
  },
];

function loadModelId(): string {
  const saved = loadJson<string>(MODEL_LS, 'ukmo_seamless');
  return OVERLAY_MODELS.some((m) => m.id === saved) ? saved : 'ukmo_seamless';
}

/**
 * Arrows by default. Three reports in three minutes asked for arrows, colour and
 * a number instead of barbs, and that is the right default on a phone. Barbs stay
 * available because they are the chart-table convention and some people read them
 * faster, but they are no longer what you get without asking.
 */
function loadWindStyle(): 'arrow' | 'barb' {
  return loadJson<string>(STYLE_LS, 'arrow') === 'barb' ? 'barb' : 'arrow';
}

function loadWaveModelId(): string {
  const saved = loadJson<string>(WAVE_LS, 'ecmwf_wam');
  return WAVE_MODELS.some((m) => m.id === saved) ? saved : 'ecmwf_wam';
}

function loadVariableId(): string {
  const saved = loadJson<string>(VAR_LS, 'wind');
  return OVERLAY_VARIABLES.some((v) => v.id === saved) ? saved : 'wind';
}

interface WeatherOverlayState {
  /** Is the overlay up at all. */
  on: boolean;
  /** Is the setup panel showing, or is it minimised to the bar. */
  panelOpen: boolean;
  modelId: string;
  /**
   * The wave model, separate from the atmospheric one on purpose. Wave height
   * comes off a different endpoint with a different set of providers, and
   * picking ECMWF for the wind says nothing about which wave model was wanted.
   */
  waveModelId: string;
  variableId: string;
  /** How the wind field is drawn. */
  windStyle: 'arrow' | 'barb';
  /** Hours ahead of now. 0 is the current hour. */
  forecastH: number;

  open: () => void;
  close: () => void;
  showPanel: () => void;
  minimise: () => void;
  setModelId: (id: string) => void;
  setWaveModelId: (id: string) => void;
  setVariableId: (id: string) => void;
  setWindStyle: (style: 'arrow' | 'barb') => void;
  setForecastH: (h: number) => void;
  nudge: (deltaH: number) => void;
}

/**
 * How far ahead we fetch, whatever the model publishes.
 *
 * ECMWF and GFS run out past a fortnight, and asking for all of it means an hourly
 * series for every grid point across sixteen days in one response, which on mobile
 * data at anchor is a lot of megabytes for a forecast nobody plans a passage on.
 * Seven days also happens to be where a marine forecast stops being a forecast and
 * starts being a trend, so the cap costs less than it looks like it does.
 */
export const FETCH_DAYS_CAP = 7;

/**
 * The furthest ahead the time bar goes, in whole hours.
 *
 * The model's own length, capped at what we fetch. It used to be the model's full
 * published run, which let the bar be nudged out to day 15 on ECMWF while only
 * three days had actually been downloaded, so the chart simply stopped changing
 * and looked frozen. A bar that can point at an hour we have no data for is a bar
 * that lies.
 */
export function maxForecastH(modelId: string): number {
  // modelById covers both catalogues, so the bar is honest for wave models too.
  const m = modelById(modelId);
  return Math.round(Math.min(m?.days ?? 3, FETCH_DAYS_CAP) * 24);
}

function clampH(h: number, modelId: string): number {
  return Math.max(0, Math.min(maxForecastH(modelId), Math.round(h)));
}

export const useWeatherOverlayStore = create<WeatherOverlayState>((set, get) => ({
  on: false,
  // Opens with the panel up, because the first thing anyone does is choose what
  // they want to look at. After that it stays out of the way.
  panelOpen: true,
  modelId: loadModelId(),
  waveModelId: loadWaveModelId(),
  variableId: loadVariableId(),
  windStyle: loadWindStyle(),
  forecastH: 0,

  open: () => set({ on: true, panelOpen: true, forecastH: 0 }),
  close: () => set({ on: false, panelOpen: true, forecastH: 0 }),
  showPanel: () => set({ panelOpen: true }),
  minimise: () => set({ panelOpen: false }),

  setModelId: (modelId) => {
    if (!OVERLAY_MODELS.some((m) => m.id === modelId)) return;
    saveJson(MODEL_LS, modelId);
    // A shorter model can leave the bar pointing past the end of its run, at an
    // hour it has no forecast for, so the hour comes back inside the new one.
    set({ modelId, forecastH: clampH(get().forecastH, modelId) });
  },
  setWaveModelId: (waveModelId) => {
    if (!WAVE_MODELS.some((m) => m.id === waveModelId)) return;
    saveJson(WAVE_LS, waveModelId);
    // Same rule as the atmospheric picker: a shorter model pulls the bar back
    // inside the hours it actually publishes.
    set({ waveModelId, forecastH: clampH(get().forecastH, waveModelId) });
  },
  setVariableId: (variableId) => {
    if (!OVERLAY_VARIABLES.some((v) => v.id === variableId)) return;
    saveJson(VAR_LS, variableId);
    set({ variableId });
  },
  setWindStyle: (windStyle) => {
    saveJson(STYLE_LS, windStyle);
    set({ windStyle });
  },
  setForecastH: (h) => set({ forecastH: clampH(h, get().modelId) }),
  nudge: (deltaH) => set({ forecastH: clampH(get().forecastH + deltaH, get().modelId) }),
}));
